import React, { useState, useEffect, useMemo } from 'react';
import { MdEdit, MdDelete, MdAdd, MdSearch } from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle, FaCog, FaTruck, FaTools, FaChartBar, FaTimes } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api`;

/* --- Menu Definitions --- */
const dropdownSections = [
  {
    id: 'masters', title: 'Masters', icon: FaCog,
    items: [
      { label: 'User Master' }, { label: 'Role Master' }, { label: 'Customer Master' },
      { label: 'Route Master' }, { label: 'Customer Price Setting' }, { label: 'State Master' },
      { label: 'Area Master' }, { label: 'Pincode Master' }, { label: 'Branch Master' },
      { label: 'Transport Master' },
    ],
  },
  {
    id: 'manifest', title: 'Manifest', icon: FaTruck,
    items: [{ label: 'Outgoing Manifest' }, { label: 'Incoming Manifest' }],
  },
  {
    id: 'transaction', title: 'Transaction', icon: FaTools,
    items: [{ label: 'Docket Confirmation' }, { label: 'Invoice' }, { label: 'Collection' }],
  },
  {
    id: 'reports', title: 'Reports', icon: FaChartBar,
    items: [{ label: 'Manifest Report' }, { label: 'Runsheet Report' }, { label: 'Delivery Report' }, { label: 'MIS Report' }],
  },
];

const STATIC_MENUS = ['Dashboard', 'Booking', 'Pick In', 'Route', 'Runsheet', 'Tracking No', 'Customer Dashboard', 'Tracking Page'];

const RoleMaster = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const initialForm = { roleName: '', description: '', menuPermissions: {} };
  const [form, setForm] = useState(initialForm);

  const token = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/role/roles`, { headers: headers() });
      const json = await res.json();
      setRoles(json.data || []);
    } catch (err) { toast.error("Failed to load roles"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token()) fetchRoles(); }, []);

  /* --- Toggle Logic for Single Row --- */
  const handleToggleRow = (menuName, isChecked) => {
    setForm(prev => ({
      ...prev,
      menuPermissions: {
        ...prev.menuPermissions,
        [menuName]: {
          view: isChecked,
          add: isChecked,
          edit: isChecked,
          delete: isChecked
        }
      }
    }));
  };

  const handleActionChange = (menu, action, checked) => {
    setForm(prev => ({
      ...prev,
      menuPermissions: {
        ...prev.menuPermissions,
        [menu]: { ...(prev.menuPermissions[menu] || {}), [action]: checked }
      }
    }));
  };

  /* --- UI Rendering Helpers --- */
  const renderMenuRow = (menuName) => {
    const perms = form.menuPermissions[menuName] || {};
    // Check if all 4 are checked to show "Select All" as active
    const isAllSelected = perms.view && perms.add && perms.edit && perms.delete;

    return (
      <div key={menuName} className="flex flex-col md:flex-row md:items-center justify-between p-3 bg-white rounded-lg border border-gray-100 shadow-sm hover:border-blue-300 transition-all">
        <div className="flex items-center gap-3 mb-2 md:mb-0">
          <input
            type="checkbox"
            checked={!!isAllSelected}
            onChange={(e) => handleToggleRow(menuName, e.target.checked)}
            className="w-4 h-4 text-green-600 rounded focus:ring-green-500 border-gray-300 cursor-pointer"
            title="Select All for this menu"
          />
          <span className="font-bold text-gray-700 text-sm">{menuName}</span>
        </div>

        <div className="flex flex-wrap gap-4 bg-gray-50 px-4 py-1.5 rounded-lg border border-gray-100">
          {['view', 'add', 'edit', 'delete'].map(action => (
            <label key={action} className="flex items-center gap-1.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={!!perms[action]}
                onChange={e => handleActionChange(menuName, action, e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-[10px] uppercase font-bold text-gray-500 group-hover:text-blue-600">{action}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return roles.filter(r => r.roleName?.toLowerCase().includes(term) || r.description?.toLowerCase().includes(term));
  }, [roles, search]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const openModal = async (role = null) => {
    if (role) {
      setEditingId(role._id);
      try {
        const res = await fetch(`${API_BASE_URL}/role/menu-access/${role._id}`, { headers: headers() });
        const json = await res.json();
        setForm({ roleName: role.roleName, description: role.description, menuPermissions: json.menuPermissions || {} });
      } catch (e) { console.error(e); }
    } else {
      setEditingId(null);
      setForm(initialForm);
    }
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/role/roles/${editingId}` : `${API_BASE_URL}/role/roles`;
      const roleRes = await fetch(url, { method, headers: headers(), body: JSON.stringify({ roleName: form.roleName, description: form.description }) });
      const roleData = await roleRes.json();
      const roleId = editingId || roleData.data._id;

      await fetch(`${API_BASE_URL}/role/menu-access`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ roleId, roleName: form.roleName, menuPermissions: form.menuPermissions })
      });

      toast.success("Permissions saved successfully");
      fetchRoles();
      setModalOpen(false);
    } catch (err) { toast.error("Failed to save"); }
    finally { setLoading(false); }
  };


  const handleDelete = async (id, roleName) => {
    // 1. Show Confirmation Popup
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the role "${roleName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2563eb', // Matches your blue-600
      cancelButtonColor: '#ef4444',  // Matches red-500
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
      reverseButtons: true, // Puts Cancel on the left, Confirm on the right
      borderRadius: '1rem'
    });

    // 2. If user confirmed, proceed with API call
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/role/roles/${id}`, {
          method: 'DELETE',
          headers: headers(),
        });

        if (res.ok) {
          // Success Popup
          toast.success('The role has been removed.');

          // Update Local State
          setRoles(prev => prev.filter(role => role._id !== id));
        } else {
          const errorData = await res.json();
          throw new Error(errorData.message || "Failed to delete");
        }
      } catch (err) {
        Swal.fire('Error', err.message || 'Something went wrong!', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      <h2 className="text-2xl font-bold mb-6 text-gray-800 uppercase tracking-tight">Role & Permission Master</h2>

      <div className="bg-white rounded-xl shadow-lg p-6">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 border-b pb-6">
          <div className="relative w-full md:w-80">
            <MdSearch className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search roles..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
            />
          </div>
          <button onClick={() => openModal()} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-bold shadow-md flex items-center gap-2 uppercase text-sm">
            <MdAdd size={20} /> Add New Role
          </button>
        </div>

        {/* Table UI */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full table-auto border-collapse">
            <thead className="bg-blue-600 text-white text-xs uppercase tracking-wider">
              <tr>
                <th className="p-4 text-left">S.No</th>
                <th className="p-4 text-left">Role Name</th>
                <th className="p-4 text-left">Description</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {paginated.map((r, i) => {
                const sNo = search ? roles.findIndex(orig => orig._id === r._id) + 1 : (page - 1) * perPage + i + 1;
                return (
                  <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 text-gray-600 font-bold">{sNo}</td>
                    <td className="p-4 font-bold text-blue-700 uppercase">{r.roleName}</td>
                    <td className="p-4 text-gray-700">{r.description}</td>
                    <td className="p-4">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-700">ACTIVE</span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => openModal(r)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-100 transition"><MdEdit size={18} /></button>
                        <button
                          onClick={() => handleDelete(r._id, r.roleName)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-100 transition"
                          title="Delete Role"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permission Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"><FaTimes size={24} /></button>

            <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-4">
              {editingId ? 'Edit Role Permissions' : 'Create New Role'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Role Name *</label>
                <input required value={form.roleName} onChange={e => setForm({ ...form, roleName: e.target.value })} className="w-full p-2.5 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full p-2.5 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-700 uppercase text-sm border-l-4 border-blue-600 pl-2">Access Control List</h3>
                <span className="text-[10px] text-gray-400 font-bold uppercase italic">* Check green box to select all for that menu</span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 max-h-[500px] overflow-y-auto">
                {/* STATIC MENUS SECTION */}
                <div className="mb-8">
                  <div className="bg-blue-600 p-2 rounded-lg font-bold text-white text-xs mb-3 uppercase tracking-wider shadow-sm">Main Dashboard Menus</div>
                  <div className="space-y-2">
                    {STATIC_MENUS.map(menuName => renderMenuRow(menuName))}
                  </div>
                </div>

                {/* DROPDOWN SECTIONS */}
                {dropdownSections.map((section, idx) => (
                  <div key={idx} className="mb-8 last:mb-0">
                    <div className="bg-blue-600 p-2 rounded-lg font-bold text-white text-xs mb-3 uppercase tracking-wider shadow-sm flex items-center gap-2">
                      <section.icon /> {section.title}
                    </div>
                    <div className="space-y-2">
                      {section.items.map(item => renderMenuRow(item.label))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 border-t pt-6">
              <button onClick={() => setModalOpen(false)} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 font-bold">CANCEL</button>
              <button onClick={submit} className="px-10 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold shadow-lg uppercase tracking-tight">SAVE PERMISSIONS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleMaster;